import { useEffect, useMemo, useState } from 'react';
import { Boxes, ChevronDown, Ellipsis, KeyRound, LifeBuoy, LogOut, Star } from 'lucide-react';
import SelectorTema from './SelectorTema';
import { useAuth } from '../lib/contextoAuth';
import { api } from '../api';
import {
  agrupar, moduloDe, abiertosIniciales, recordarAbiertos, registrarUso, masUsadas, partir,
} from '../experiencia/menu';
import MarcaDePlan from '../experiencia/MarcaDePlan';

/* =====================================================================
   OCHO MÓDULOS PLEGABLES · DIRECTRIZ-EXPERIENCIA E2 (16 sep 2026)
   =====================================================================
   Medido antes de tocar: 55 opciones a la vez en 11 grupos, ninguno
   plegable, y en teléfono 57 objetivos de menos de 44 px. Ahora los
   grupos de los carriles se funden en ocho módulos (menu.js), cada uno
   se pliega, y se abren el del módulo activo, los que la persona dejó
   abiertos y los de su rol. Arriba, «Lo que más usas». Lo apagado por
   módulo de la cuenta no aparece. Cada opción mide al menos 44 px en
   teléfono (regla 9 del usuario cero).
   ===================================================================== */

// Barra lateral fija (pantalla ancha) o cajón (teléfono). Módulos agrupados
// con icono + etiqueta; al final, separadas por una línea, las opciones de
// cuenta.
export default function BarraLateral({
  grupos, activa, onSelect, abierta, onCerrar, onPassword, onSalir, onReportar,
}) {
  const { usuario } = useAuth();
  const [apagados, setApagados] = useState([]);
  // ANEXO-CLIENTE-01 H-6: cuántas veces abrió LA EMPRESA cada pantalla
  // (servidor, sin persona). Ordena cada módulo; la cola va a «Más opciones».
  const [usoEmpresa, setUsoEmpresa] = useState(null);
  const [desplegados, setDesplegados] = useState(() => new Set());
  // Lo apagado se PINTA con su marca (corona / diamante / candado), no se
  // esconde ni se pliega: DIRECTRIZ-LOOP L1-3. Ver `MarcaDePlan.jsx`.
  const [bloqueados, setBloqueados] = useState({});
  // Cada pantalla abierta cuenta un uso (en el navegador de la persona).
  const uso = useMemo(() => registrarUso(activa), [activa]);
  const modulos = useMemo(() => agrupar(grupos, apagados, usoEmpresa, uso, bloqueados),
    [grupos, apagados, usoEmpresa, uso, bloqueados]);
  const moduloActivo = moduloDe(modulos, activa);
  const [abiertos, setAbiertos] = useState(() => abiertosIniciales(usuario?.rol, moduloActivo));
  // El módulo de la pantalla activa se ve abierto siempre, sin tocar lo
  // que la persona dejó abierto o cerrado.
  const visibles = useMemo(() => (moduloActivo ? new Set([...abiertos, moduloActivo]) : abiertos), [abiertos, moduloActivo]);

  useEffect(() => {
    let vigente = true;
    api.get('/experiencia/menu').then((r) => {
      if (!vigente) return;
      setApagados(r.apagados || []);
      setUsoEmpresa(r.uso || {});
      setBloqueados(r.bloqueados || {});
    }).catch(() => {});
    return () => { vigente = false; };
  }, []);

  // Y cada apertura se cuenta también en el servidor, para la empresa. Si
  // falla no pasa nada: el menú sigue con el orden que tenía.
  useEffect(() => {
    if (!activa || activa.startsWith('__')) return;
    api.post('/experiencia/uso', { pantalla: activa }).catch(() => {});
  }, [activa]);

  function alternar(modulo) {
    setAbiertos(() => {
      const n = new Set(visibles);
      if (n.has(modulo)) n.delete(modulo); else n.add(modulo);
      recordarAbiertos(n);
      return n;
    });
  }
  // «LO QUE MÁS USAS» SE CONGELA AL ENTRAR (T-18). Antes se recalculaba en
  // cada navegación —`uso` cambia con `activa`, y `masUsadas` reordena—, así
  // que la lista de atajos se movía bajo el dedo justo cuando se iba a tocar
  // uno: se abría el que quedó donde estaba el otro. Ahora el ORDEN se fija
  // al montar la barra (al entrar o recargar) y no se toca durante la
  // sesión; el conteo se sigue guardando, así que la PRÓXIMA entrada lo
  // recalcula. Se guardan los ids y se mapean a su item vigente en cada
  // render, para que la marca de plan (corona/candado) siga al día.
  // El inicializador de `useState` corre UNA sola vez, al montar la barra:
  // ahí se fija el orden y ya no se vuelve a calcular en toda la sesión.
  const [frecuentesIds] = useState(() => masUsadas(modulos, uso).map((i) => i.id));
  const itemsPorId = useMemo(() => {
    const m = new Map();
    for (const mod of modulos) for (const i of mod.items) m.set(i.id, i);
    return m;
  }, [modulos]);
  const frecuentes = frecuentesIds
    .map((id) => itemsPorId.get(id))
    .filter(Boolean);

  const boton = (m) => {
    const Icono = m.icono;
    const act = activa === m.id;
    return (
      <button
        key={m.id}
        type="button"
        onClick={() => onSelect(m.id)}
        aria-current={act ? 'page' : undefined}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-lg text-sm font-medium transition-colors ${
          act ? 'bg-[var(--color-marca-suave)] text-[var(--color-marca)]' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Icono className="h-5 w-5 shrink-0" aria-hidden="true" /> {m.etiqueta}
        {m.bloqueo && <span className="ml-auto"><MarcaDePlan marca={m.bloqueo.marca} /></span>}
      </button>
    );
  };

  return (
    <>
      {abierta && (
        <div className="fixed inset-0 bg-black/30 z-30 md:hidden" aria-hidden="true" onClick={onCerrar} />
      )}
      {/* EL CAJÓN CERRADO SEGUÍA EN EL RECORRIDO DEL TABULADOR. En teléfono,
          con el menú cerrado, tabular metía el foco en catorce botones que
          estaban fuera de la pantalla: se tecleaba a ciegas sin ver nada
          moverse. `cajon-cerrado` lo saca con `visibility`, que sí quita del
          orden de tabulación, y lo devuelve en pantalla ancha, donde el menú
          está siempre a la vista. */}
      <aside
        className={`cajon fixed top-0 left-0 h-full w-60 bg-white border-r border-gray-200 z-40 flex flex-col transition-transform md:translate-x-0 ${
          abierta ? 'translate-x-0' : '-translate-x-full cajon-cerrado'
        }`}
      >
        <div className="h-14 flex items-center gap-2 px-4 border-b border-gray-200">
          <div className="bg-[var(--color-marca)] rounded-lg p-1.5"><Boxes className="h-5 w-5 text-white" /></div>
          <span className="font-semibold text-gray-900">TEGO Cloud</span>
        </div>

        <nav aria-label="Módulos" className="flex-1 overflow-y-auto p-3 space-y-2">
          {frecuentes.length > 0 && (
            <section aria-label="Lo que más usas" className="mb-2">
              <h2 className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider px-2 mb-1">
                <Star className="h-3 w-3" aria-hidden="true" /> Lo que más usas
              </h2>
              <div className="space-y-0.5">{frecuentes.map(boton)}</div>
            </section>
          )}
          {modulos.map((g) => {
            const abiertoM = visibles.has(g.modulo);
            const contieneActiva = g.items.some((m) => m.id === activa);
            const idLista = `menu-${g.modulo.replace(/\W+/g, '-').toLowerCase()}`;
            const { arriba, mas, activaEscondida } = partir(g.items, activa);
            const verTodo = mas.length === 0 || desplegados.has(g.modulo) || activaEscondida;
            return (
              <section key={g.modulo} data-modulo={g.modulo}>
                <h2 className="m-0">
                  <button
                    type="button"
                    aria-expanded={abiertoM}
                    aria-controls={idLista}
                    onClick={() => alternar(g.modulo)}
                    className={`w-full flex items-center justify-between px-2 py-2 min-h-11 md:min-h-8 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
                      contieneActiva && !abiertoM ? 'text-[var(--color-marca)]' : 'text-gray-600'} hover:bg-gray-50`}
                  >
                    <span>{g.modulo} <span className="font-normal normal-case tracking-normal text-gray-500">· {g.items.length}</span></span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${abiertoM ? '' : '-rotate-90'}`} aria-hidden="true" />
                  </button>
                </h2>
                {abiertoM && (
                  <div id={idLista} className="space-y-0.5">
                    {(verTodo ? g.items : arriba).map(boton)}
                    {mas.length > 0 && !verTodo && (
                      <button type="button"
                        onClick={() => setDesplegados((s) => new Set([...s, g.modulo]))}
                        aria-expanded={false}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                        <Ellipsis className="h-5 w-5 shrink-0" aria-hidden="true" /> Más opciones · {mas.length}
                      </button>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </nav>

        <div className="border-t border-gray-200 p-3 space-y-0.5">
          <SelectorTema />
          {/* PEDIR AYUDA A TI, entrada fija del menú (hallazgo del dueño, 26
              sep 2026): antes solo estaba el flotante, que en pantalla ancha
              tapaba esta misma barra. Ahora vive aquí y en la cabecera. */}
          {onReportar && (
            <button type="button" onClick={onReportar} className="w-full flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              <LifeBuoy className="h-5 w-5" aria-hidden="true" /> Pedir ayuda a TI
            </button>
          )}
          <button type="button" onClick={onPassword} className="w-full flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            <KeyRound className="h-5 w-5" aria-hidden="true" /> Cambiar contraseña
          </button>
          <button type="button" onClick={onSalir} className="w-full flex items-center gap-2.5 px-2.5 py-2 min-h-11 md:min-h-9 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50">
            <LogOut className="h-5 w-5" aria-hidden="true" /> Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}
